import { VideocamOffOutlined } from "@mui/icons-material";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  selectCameraPreview,
  selectCurrentEnvironmentId,
} from "../../app/slices/EnvironmentSlice";
import { RootState } from "../../app/Store";
import { useCameraPreview } from "../../hooks/GetCameraPreviewHook";
import Skeleton from "@mui/material/Skeleton";

/** 16:9 render of an environment camera, fetched once and cached in EnvironmentSlice. */
export default function CameraRenderPreview(props: {
  cameraId: string;
  className?: string;
  /** Overlaid on the top-left of the render, e.g. an "In use" chip. */
  badge?: React.ReactNode;
  /** Thumbnail size (menus, lists): the empty state keeps only its icon. */
  compact?: boolean;
}) {
  const { cameraId, className, badge, compact } = props;
  const { t } = useTranslation();
  const environmentId = useSelector(selectCurrentEnvironmentId);
  const cachedPreview = useSelector((state: RootState) =>
    selectCameraPreview(state, cameraId)
  );
  const [getCameraPreview] = useCameraPreview(cameraId);
  const [missingCameraId, setMissingCameraId] = useState<string>();
  const [loadedUri, setLoadedUri] = useState<string>();
  const [failedUri, setFailedUri] = useState<string>();

  useEffect(() => {
    if (!cameraId || cachedPreview !== undefined) return;
    let cancelled = false;
    getCameraPreview(environmentId)
      .then((uri) => {
        if (!cancelled && !uri) setMissingCameraId(cameraId);
      })
      .catch(() => {
        if (!cancelled) setMissingCameraId(cameraId);
      });
    return () => {
      cancelled = true;
    };
  }, [cameraId, cachedPreview, environmentId, getCameraPreview]);

  const hasImage = !!cachedPreview && failedUri !== cachedPreview;
  const isMissing =
    !cameraId ||
    missingCameraId === cameraId ||
    (!!cachedPreview && failedUri === cachedPreview);
  const isLoading = !isMissing && loadedUri !== cachedPreview;

  return (
    <div
      className={classNames(
        "relative aspect-video overflow-hidden bg-creator-surfaceHover",
        compact ? "rounded-[4px]" : "rounded-[6px]",
        className
      )}
      data-testid="camera-render-preview"
    >
      {hasImage && (
        <img
          key={cachedPreview}
          src={cachedPreview}
          alt=""
          className={classNames(
            "w-full h-full object-cover pointer-events-none select-none",
            { invisible: isLoading }
          )}
          onLoad={() => setLoadedUri(cachedPreview)}
          onError={() => setFailedUri(cachedPreview)}
        />
      )}
      {isLoading && (
        <div
          className="absolute inset-0 overflow-hidden"
          role="status"
          aria-label={String(t("camera_preview_loading", "Loading camera preview…"))}
        >
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        </div>
      )}
      {isMissing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-[4px] text-[12px] text-creator-text-sub">
          <VideocamOffOutlined fontSize="small" sx={compact ? { fontSize: 14 } : undefined} />
          {!compact &&
            (cameraId
              ? t("camera_preview_unavailable", "No preview rendered yet")
              : t("set_camera_none", "No camera selected"))}
        </div>
      )}
      {badge && <div className="absolute left-[6px] top-[6px]">{badge}</div>}
    </div>
  );
}
